import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Minus, Plus } from 'lucide-react';
import { workExperience } from '../data/data';
import { staggerContainer, fadeInUp } from '../lib/motion';

const Experience = () => {
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section id="experience" className="py-24 bg-[#f8f8f6]">
      <div className="container-editorial">
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-8 mb-20">
          <div className="col-span-12 lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs uppercase tracking-widest text-[#666666] mb-4" style={{ fontFamily: 'var(--font-sans)' }}>
                Experience
              </p>
              <h2 className="text-5xl font-normal text-[#1a1a1a]" style={{ fontFamily: 'var(--font-serif)' }}>
                Career
              </h2>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-8">
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <motion.div
              className="space-y-0"
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
            >
              {workExperience.map((job, index) => (
                <motion.div
                  key={job.id}
                  variants={fadeInUp}
                  className="border-t border-[#d4d4d0] py-8"
                >
                  <div className="grid grid-cols-12 gap-y-8 lg:gap-x-8">
                    <motion.div
                      className="col-span-12 lg:col-span-3"
                      whileHover={{ x: 4, transition: { duration: 0.2 } }}
                    >
                      <p className="text-sm text-[#666666] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>
                        {job.duration}
                      </p>
                    </motion.div>

                    <div className="col-span-12 lg:col-span-9">
                      <motion.h3
                        className="text-3xl font-normal text-[#1a1a1a] mb-2"
                        style={{ fontFamily: 'var(--font-serif)' }}
                        whileHover={{ color: '#8B4513', transition: { duration: 0.3 } }}
                      >
                        {job.title}
                      </motion.h3>
                      <p className="text-base text-[#8B4513] mb-4" style={{ fontFamily: 'var(--font-sans)', fontWeight: 500 }}>
                        <a
                          href={job?.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={!job?.link && "disabled"}
                        >
                          {job.company}
                        </a>
                      </p>
                      <p className="text-base text-[#666666] mb-6 leading-relaxed" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>
                        {job.summary}
                      </p>

                      <motion.button
                        onClick={() => toggleExpand(job.id)}
                        className="inline-flex items-center gap-2 text-sm text-[#1a1a1a] hover:text-[#8B4513] transition-colors duration-300 uppercase tracking-wide"
                        style={{ fontFamily: 'var(--font-sans)' }}
                        whileHover={{ x: 2, transition: { duration: 0.2 } }}
                      >
                        {expandedId === job.id ? (
                          <>
                            <Minus className="w-3 h-3" />
                            Hide Impact
                          </>
                        ) : (
                          <>
                            <Plus className="w-3 h-3" />
                            View Impact
                          </>
                        )}
                      </motion.button>

                      <AnimatePresence>
                        {expandedId === job.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                            className="overflow-hidden"
                          >
                            <motion.ul
                              className="mt-6 space-y-3 pl-0"
                              initial="initial"
                              animate="animate"
                              variants={staggerContainer}
                            >
                              {job.highlights.map((highlight, idx) => (
                                <motion.li
                                  key={idx}
                                  className="flex items-start gap-4 text-base text-[#666666] leading-relaxed"
                                  style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}
                                  variants={fadeInUp}
                                >
                                  <span className="w-1 h-1 bg-[#8B4513] mt-3 flex-shrink-0" />
                                  <span>{highlight}</span>
                                </motion.li>
                              ))}
                            </motion.ul>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;